import React, { useState, useEffect, useRef } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import userManagementService from '../services/admin/userManagementService';

const UserManagementTest = () => {
    // Check if user has admin role
    const userRolesData = localStorage.getItem('userRoles');
    let isAdmin = false;

    if (userRolesData) {
        try {
            const roles = JSON.parse(userRolesData);
            const adminRoles = ['admin', 'tenant_admin', 'super_admin', 'manager'];
            if (Array.isArray(roles)) {
                isAdmin = roles.some(role => adminRoles.includes(role.toLowerCase()));
            }
        } catch (error) {
            console.error('Error parsing user roles:', error?.message || 'Unknown error');
        }
    }

    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);

    // Pagination state
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);

    // Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [selectedRole, setSelectedRole] = useState(null);

    // Role management states
    const [showRoleDialog, setShowRoleDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [newRole, setNewRole] = useState(null);
    const [updatingRole, setUpdatingRole] = useState(false);
    const [availableRoles, setAvailableRoles] = useState([]);

    const toast = useRef(null);

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setFirst(0); // Reset to first page on search
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Fetch users on component mount and when dependencies change
    useEffect(() => {
        fetchUsers();
    }, [first, rows, debouncedSearch, selectedRole]);

    // Fetch available roles on component mount
    useEffect(() => {
        fetchAvailableRoles();
    }, []);

    const fetchUsers = async () => {
        setUsersLoading(true);
        try {
            const page = Math.floor(first / rows) + 1;

            const params = {
                page: page,
                per_page: rows
            };

            if (debouncedSearch && debouncedSearch.trim()) {
                params.search = debouncedSearch.trim();
            }

            if (selectedRole) {
                params.role = selectedRole;
            }

            const response = await userManagementService.getUserList(params);

            if (response.success) {
                setUsers(response.data || []);

                if (response.pagination) {
                    setTotalRecords(response.pagination.total);
                } else {
                    setTotalRecords(response.data ? response.data.length : 0);
                }
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: response.message || 'Failed to fetch users',
                    life: 5000
                });
                setUsers([]);
                setTotalRecords(0);
            }
        } catch (error) {
            console.error('Error fetching users:', error?.message || 'Unknown error');
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error?.message || 'Failed to fetch users',
                life: 5000
            });
            setUsers([]);
            setTotalRecords(0);
        } finally {
            setUsersLoading(false);
        }
    };

    const fetchAvailableRoles = async () => {
        try {
            const response = await userManagementService.getAvailableRoles();
            if (response.success) {
                setAvailableRoles(response.data || []);
            }
        } catch (error) {
            console.error('Error fetching roles:', error?.message || 'Unknown error');
        }
    };

    const clearFilters = () => {
        setSearchTerm('');
        setSelectedRole(null);
        setFirst(0);
    };

    const closeRoleDialog = () => {
        setShowRoleDialog(false);
        setSelectedUser(null);
        setNewRole(null);
    };

    const handleRoleUpdate = async () => {
        if (!selectedUser || !newRole) return;

        setUpdatingRole(true);
        try {
            const response = await userManagementService.updateUserRole(selectedUser.id, {
                role: newRole
            });

            if (response.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'User role updated successfully',
                    life: 3000
                });
                closeRoleDialog();
                fetchUsers(); // Refresh user list
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: response.message || 'Failed to update role',
                    life: 5000
                });
            }
        } catch (error) {
            console.error('Error updating role:', error?.message || 'Unknown error');
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || error?.message || 'Failed to update user role',
                life: 5000
            });
        } finally {
            setUpdatingRole(false);
        }
    };

    // Column templates
    const nameBodyTemplate = (rowData) => {
        return (
            <div className="flex align-items-center gap-2">
                <div className="flex flex-column">
                    <span className="font-medium">{rowData.name}</span>
                </div>
            </div>
        );
    };

    const emailBodyTemplate = (rowData) => (
        <span className="text-900">{rowData.email}</span>
    );

    const roleTemplate = (rowData) => {
        const role = rowData.primary_role || 'user';
        const severityMap = {
            tenant_admin: 'danger',
            super_admin: 'danger',
            admin: 'danger',
            manager: 'warning',
        };
        const severity = severityMap[role] || 'info';
        return (
            <Tag
                value={role.replace(/_/g, ' ').toUpperCase()}
                severity={severity}
            />
        );
    };

    const joinedBodyTemplate = (rowData) => {
        if (!rowData.created_at) return <span className="text-500">-</span>;
        const date = new Date(rowData.created_at);
        const formatted = new Intl.DateTimeFormat('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        }).format(date);
        return <span className="text-700 text-sm">{formatted}</span>;
    };

    const actionTemplate = (rowData) => (
        <div className="flex flex-wrap justify-content-center gap-1">
            <Button
                icon="pi pi-user-edit"
                severity="info"
                text
                size="small"
                onClick={() => {
                    setSelectedUser(rowData);
                    setNewRole(rowData.primary_role);
                    setShowRoleDialog(true);
                }}
                tooltip="Change Role"
                tooltipOptions={{ position: 'top' }}
                aria-label={`Change role for ${rowData.name}`}
            />
        </div>
    );

    // Access control check
    if (!isAdmin) {
        return (
            <div className="grid">
                <div className="col-12">
                    <Card title="Access Denied">
                        <Message
                            severity="error"
                            text="You need admin privileges to access this page."
                        />
                    </Card>
                </div>
            </div>
        );
    }

    if (usersLoading && users.length === 0) {
        return (
            <div className="flex align-items-center justify-content-center p-5">
                <ProgressSpinner />
            </div>
        );
    }

    const roleOptions = [
        { label: 'All Roles', value: null },
        { label: 'Tenant Admin', value: 'tenant_admin' },
        { label: 'Manager', value: 'manager' },
        { label: 'User', value: 'user' }
    ];

    return (
        <>
            <Toast ref={toast} position="top-right" />

            <div className="grid">
                <div className="col-12">
                    {/* Page header — stacks on mobile, side-by-side from md */}
                    <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-3 mb-4">
                        <div>
                            <h1 className="text-xl font-semibold text-900 m-0 mb-1">User Role Management</h1>
                            <p className="text-600 text-sm m-0">
                                Manage user roles and permissions
                            </p>
                        </div>
                    </div>

                    <Card>
                        {/* Filter bar — 5-slot grid: 2 filters + 2 spacers + action block (P7 golden rule) */}
                        <div className="surface-ground p-3 border-round mb-3">
                            <div className="grid">
                                <div className="col-6 md:col">
                                    <IconField iconPosition="left" className="w-full">
                                        <InputIcon className="pi pi-search" />
                                        <InputText
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            placeholder="Search by name or email..."
                                            aria-label="Search users"
                                            className="w-full p-inputtext-sm"
                                        />
                                    </IconField>
                                </div>
                                <div className="col-6 md:col">
                                    <Dropdown
                                        value={selectedRole}
                                        options={roleOptions}
                                        optionLabel="label"
                                        optionValue="value"
                                        onChange={(e) => {
                                            setSelectedRole(e.value);
                                            setFirst(0);
                                        }}
                                        placeholder="All Roles"
                                        className="w-full p-inputtext-sm"
                                        showClear
                                        aria-label="Filter by role"
                                    />
                                </div>
                                <div className="hidden md:block md:col" aria-hidden="true"></div>
                                <div className="hidden md:block md:col" aria-hidden="true"></div>
                                <div className="col-12 md:col flex gap-2">
                                    <Button
                                        label="Clear"
                                        icon="pi pi-filter-slash"
                                        severity="secondary"
                                        outlined
                                        size="small"
                                        onClick={clearFilters}
                                        disabled={!searchTerm && !selectedRole}
                                        aria-label="Clear filters"
                                        className="flex-1 py-1 md:py-2"
                                    />
                                    <Button
                                        icon="pi pi-refresh"
                                        label="Refresh"
                                        severity="info"
                                        outlined
                                        size="small"
                                        onClick={fetchUsers}
                                        tooltip="Refresh user list"
                                        tooltipOptions={{ position: 'top' }}
                                        aria-label="Refresh user list"
                                        className="flex-1 py-1 md:py-2"
                                    />
                                </div>
                            </div>
                        </div>

                        <DataTable
                            value={users}
                            lazy
                            loading={usersLoading}
                            paginator
                            first={first}
                            rows={rows}
                            totalRecords={totalRecords}
                            onPage={(e) => {
                                setFirst(e.first);
                                setRows(e.rows);
                            }}
                            rowsPerPageOptions={[10, 25, 50]}
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                            currentPageReportTemplate="Showing {first}-{last} of {totalRecords}"
                            paginatorClassName="text-sm"
                            pageLinkSize={3}
                            dataKey="id"
                            className="p-datatable-gridlines"
                            responsiveLayout="scroll"
                            scrollable
                            scrollHeight="flex"
                            stripedRows
                            size="small"
                            showGridlines
                            emptyMessage={
                                <div className="flex flex-column align-items-center py-5">
                                    <i className="pi pi-users pi-2x text-400 mb-3" />
                                    <span className="text-600">No users found.</span>
                                </div>
                            }
                        >
                            <Column
                                field="id"
                                header="ID"
                                sortable
                                frozen
                                alignFrozen="left"
                                style={{ minWidth: '80px' }}
                            />
                            <Column
                                field="name"
                                header="Name"
                                body={nameBodyTemplate}
                                sortable
                                frozen
                                alignFrozen="left"
                                style={{ minWidth: '200px' }}
                            />
                            <Column
                                field="email"
                                header="Email"
                                body={emailBodyTemplate}
                                sortable
                                style={{ minWidth: '220px' }}
                            />
                            <Column
                                field="primary_role"
                                header="Role"
                                body={roleTemplate}
                                sortable
                                style={{ minWidth: '130px' }}
                            />
                            <Column
                                field="created_at"
                                header="Joined"
                                body={joinedBodyTemplate}
                                sortable
                                style={{ minWidth: '130px' }}
                            />
                            <Column
                                header="Actions"
                                body={actionTemplate}
                                style={{ minWidth: '180px' }}
                                alignHeader="center"
                                bodyClassName="text-center"
                            />
                        </DataTable>
                    </Card>
                </div>
            </div>

            {/* Role Update Dialog */}
            <Dialog
                visible={showRoleDialog}
                onHide={() => { if (!updatingRole) closeRoleDialog(); }}
                header={selectedUser ? `Update Role — ${selectedUser.name}` : 'Update Role'}
                modal
                draggable={false}
                closable={!updatingRole}
                style={{ width: '90vw', maxWidth: '500px' }}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            severity="secondary"
                            outlined
                            size="small"
                            onClick={closeRoleDialog}
                            disabled={updatingRole}
                        />
                        <Button
                            label="Update Role"
                            icon="pi pi-check"
                            onClick={handleRoleUpdate}
                            loading={updatingRole}
                            disabled={!newRole || updatingRole || newRole === selectedUser?.primary_role}
                            size="small"
                        />
                    </div>
                }
            >
                <div className="field">
                    <label htmlFor="role" className="block font-medium text-sm mb-2">
                        Select New Role <span className="text-red-500">*</span>
                    </label>
                    <Dropdown
                        id="role"
                        value={newRole}
                        options={availableRoles}
                        onChange={(e) => setNewRole(e.value)}
                        placeholder="Choose a role"
                        className="w-full p-inputtext-sm"
                        showClear
                    />
                    {selectedUser && (
                        <small className="text-500 block mt-2">
                            Current role: <strong>{selectedUser.primary_role || 'user'}</strong>
                        </small>
                    )}
                </div>
            </Dialog>
        </>
    );
};

export default UserManagementTest;
